import {
  computed,
  Ref,
  inject,
  provide,
  ComputedRef,
  ref,
  WritableComputedRef
} from "vue";

import { Market, Option, BetData, SportEvent } from "@fresh8/types/src/betData";
import { orderedCompetitorData } from "./lib/orderedCompetitorData";
import { sortOptionsByOdds } from "./lib/sortOptions";
import * as generateLinks from "./lib/generateLinks";
import {
  Tracking,
  Macro,
  Operator,
  AdTagCampaign,
  AdTag
} from "@fresh8/types/src/";
import { injectAdTag, injectBrand } from "./brand";
import {
  injectTemplateSize,
  MOBILE_LEADERBOARD,
  MOBILE_LEADERBOARD_FIXED
} from "@fresh8/ad-data-composition/src/useTemplateSize";

const HANDICAP_BETTING = "Handicap Betting";
const MATCH_WINNER = "Match Winner";
const OVER_UNDER_TOTALS = "Over/Under Totals";

export function createBetData(
  betData: Ref<BetData | undefined>,
  adTagTracking: Ref<Record<string, Tracking> | undefined>,
  adTagTeam: Ref<string | undefined>,
  macros: Ref<Macro[]> = ref([]),
  stake: Ref<number>,
  adTagOperator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>,
  refetch: (
    adTag: Ref<AdTag>,
    operator:
      | WritableComputedRef<Operator | undefined>
      | Ref<Operator | undefined>,
    campaign: Ref<AdTagCampaign | undefined>
  ) => {
    result: Ref<BetData | undefined>;
    loading: Ref<boolean>;
    error: Ref<boolean>;
  }
) {
  const markets = computed(() => betData?.value?.markets);
  const recommender_detail = computed(() => betData?.value?.recommender_detail);
  const marketNames = computed(
    () =>
      (markets.value &&
        Object.values(markets.value as Record<string, Market>).map(
          market => market.marketType
        )) ||
      []
  );

  const operator = computed(() => {
    return (
      betData.value?.operator || {
        name: adTagOperator.value?.key,
        friendlyName: adTagOperator.value?.name,
        _id: adTagOperator.value?._id
      }
    );
  });

  const operatorTracking = computed(() => {
    if (operator.value && operator.value._id) {
      return adTagTracking.value?.[operator.value._id];
    }
  });

  const operators = computed(() => betData?.value?.operators);

  const searchParams = new URLSearchParams(window.location.search);
  const clickUrl = computed(() =>
    generateLinks.getClickUrl(
      operatorTracking?.value?.clickThroughUrl,
      operatorTracking?.value?.clickThroughBehaviour,
      searchParams.get("clickUrl") || ""
    )
  );

  const targetUrl = computed(() =>
    generateLinks.getTargetUrl(
      operatorTracking?.value?.targetUrl || "",
      betData?.value?.targetUrl || "",
      searchParams.get("targetUrl") || "",
      operatorTracking?.value?.fallbackUrl || "",
      operatorTracking?.value?.targetUrlType === "deeplink"
    )
  );

  const prerollTargetUrl = computed(
    () => operatorTracking?.value?.prerollTargetUrl
  );

  const affiliateId = computed(() =>
    generateLinks.getAffiliateId(
      operatorTracking?.value?.affiliateCode || "",
      searchParams.get("aff") || "",
      adTagTeam.value || "",
      macros.value
    )
  );

  const events = computed(() => {
    return (
      betData?.value?.events &&
      generateLinks.events(betData.value.events, {
        clickUrl: clickUrl.value,
        targetUrl: targetUrl.value,
        affiliateId: affiliateId.value,
        operatorKey: operator.value?.name || ""
      })
    );
  });

  const options = computed(() => {
    return (
      betData?.value?.options &&
      generateLinks.options(betData.value.options, betData.value.markets, {
        clickUrl: clickUrl.value,
        targetUrl: targetUrl.value,
        affiliateId: affiliateId.value,
        operatorKey: operator.value?.name || "",
        stake: stake.value
      })
    );
  });

  const links = computed(() => {
    return generateLinks.links({
      clickUrl: clickUrl.value,
      targetUrl: targetUrl.value,
      affiliateId: affiliateId.value,
      operatorKey: operator.value?.name || ""
    });
  });

  return {
    events,
    operator,
    operators,
    eventIds: computed(() => (events.value && Object.keys(events.value)) || []),
    markets,
    options,
    affiliateId,
    marketNames,
    links,
    betData,
    clickUrl,
    targetUrl,
    prerollTargetUrl,
    refetch,
    recommender_detail
  };
}

export type BetDataContext = ReturnType<typeof createBetData>;

export const BetDataSymbol = Symbol("BetDataCompSymbol");

export function provideBetData(
  betData: Ref<BetData | undefined>,
  adTagTracking: Ref<Record<string, Tracking> | undefined>,
  adTagTeam: Ref<string | undefined>,
  macros: Ref<Macro[]>,
  stake: Ref<number>,
  operator:
    | WritableComputedRef<Operator | undefined>
    | Ref<Operator | undefined>,
  refetch: (
    adTag: Ref<AdTag>,
    operator:
      | WritableComputedRef<Operator | undefined>
      | Ref<Operator | undefined>,
    campaign: Ref<AdTagCampaign | undefined>
  ) => {
    result: Ref<BetData | undefined>;
    loading: Ref<boolean>;
    error: Ref<boolean>;
  }
): BetDataContext {
  const _betdata = createBetData(
    betData,
    adTagTracking,
    adTagTeam,
    macros,
    stake,
    operator,
    refetch
  );

  provide(BetDataSymbol, _betdata);

  return _betdata;
}

export function injectBetData(): BetDataContext | undefined {
  return inject<BetDataContext>(BetDataSymbol);
}

export function getHomeAndAwayOptionsForMarket(
  market: Market | undefined,
  options: Record<string, Option> | undefined
): { home: string; away: string; draw: string } {
  if (!options || !market) {
    return { home: "", draw: "", away: "" };
  }

  return market.options?.reduce(
    (acc, curr) => {
      const option = options[curr];
      return {
        ...acc,
        [option.label]: curr
      };
    },
    { home: "", draw: "", away: "" }
  );
}

export function composeMarket(
  marketId: string | undefined
): ComputedRef<Market | undefined> {
  const betData = injectBetData();

  return computed(
    () => (!!marketId && betData?.markets.value?.[marketId]) || undefined
  );
}

export function composeEventMarkets(eventId: string): ComputedRef<string[]> {
  const betData = injectBetData();

  return computed(() => betData?.events.value?.[eventId]?.markets || []);
}

export function composeEvent(
  eventId: string | undefined
): ComputedRef<SportEvent | undefined> {
  const betData = injectBetData();

  return computed(() => {
    if (eventId && betData?.events.value) {
      return betData.events.value[eventId];
    }
  });
}

const injectEventsSymbol = Symbol("inject-events");
export function provideEvents(eventIds: string[]): void {
  const events = eventIds.map(id => composeEvent(id));
  provide(injectEventsSymbol, events);
}
export function injectEvents(): ComputedRef<SportEvent[]> {
  return inject(
    injectEventsSymbol,
    computed(() => [{}] as SportEvent[])
  );
}

const injectEventSymbol = Symbol("inject-event");
export function provideEvent(eventId: string): void {
  provide(injectEventSymbol, composeEvent(eventId));
}
export function injectEvent(): ComputedRef<SportEvent> {
  return inject(
    injectEventSymbol,
    computed(() => ({} as SportEvent))
  );
}

/**
 * Check if the current card has a league logo related to it
 * @returns {ComputedRef<boolean>}
 */
export function checkUseLeagueLogo(): ComputedRef<boolean> {
  // debugger;
  const templateSizeData = injectTemplateSize();
  if (
    templateSizeData?.size.value.name === MOBILE_LEADERBOARD.name ||
    templateSizeData?.size.value.name === MOBILE_LEADERBOARD_FIXED.name
  )
    return computed(() => false);
  const brandData = injectBrand();
  const adTagData = injectAdTag();
  const eventData = injectEvent();

  const useLeagueLogo = computed(() => {
    const leagueLogos =
      brandData?.brand.value?.adTemplate.team?.badge?.leagueLogos || [];
    const operator = adTagData?.operator.value?._id;
    const leagueLogo = leagueLogos.find(l => l.operator === operator);

    if (!leagueLogo) return false;

    return !!(
      leagueLogo?.competitions &&
      leagueLogo.competitions?.find(c => c === eventData.value?.competition)
    );
  });

  return useLeagueLogo;
}

/**
 * Returns the league logo image src
 * @returns {ComputedRef<string|undefined>}
 */
export function getLeagueLogoSrc(): ComputedRef<string | undefined> {
  const templateSizeData = injectTemplateSize();
  if (
    templateSizeData?.size.value.name === MOBILE_LEADERBOARD.name ||
    templateSizeData?.size.value.name === MOBILE_LEADERBOARD_FIXED.name
  )
    return computed(() => undefined);
  const baseUrl = "https://cdn.fresh8.co/assets/mercury-assets/leagues/";
  const brandData = injectBrand();
  const adTagData = injectAdTag();
  const betData = injectBetData();
  const eventsData = computed(() => Object.values(betData?.events.value || {}));
  const leagueLogoSrc = computed(() => {
    const leagueLogo =
      brandData?.adTemplate.value?.team?.badge.leagueLogos?.find(
        l => l.operator === adTagData?.operator.value?._id
      ) || ({} as { operator?: string; competitions?: string[] });

    const event =
      eventsData?.value?.find &&
      eventsData.value.find(
        e => !!leagueLogo.competitions?.find(l => l === e.normalisedCompetition)
      );
    if (event)
      return `${baseUrl}${event.normalisedCompetition.replaceAll(
        " ",
        "-"
      )}.png`;
    else return undefined;
  });
  return leagueLogoSrc;
}

export function composeBetOption(optionId: string) {
  const betData = injectBetData();

  return computed(() => betData?.options.value?.[optionId]);
}

export function composeMultiFtrMarkets(eventId: string) {
  const betData = injectBetData();
  const eventMarkets = composeEventMarkets(eventId);

  return computed(() => {
    const matchWinner = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value?.[marketEvent].marketType === MATCH_WINNER
    );

    const handicapBetting = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value?.[marketEvent].marketType === HANDICAP_BETTING
    );

    const overUnderTotals = eventMarkets.value.find(
      marketEvent =>
        betData?.markets.value?.[marketEvent].marketType === OVER_UNDER_TOTALS
    );

    return [matchWinner, handicapBetting, overUnderTotals];
  });
}

const name = ["Moneyline", "Spread", "Totals"];

export function composeHomeAwayOptions(
  marketIds: Ref<(string | undefined)[]>
): ComputedRef<
  {
    displayName: string;
    marketId: string;
    options: string[];
  }[]
> {
  const betData = injectBetData();

  return computed(() =>
    marketIds.value.map((id, index) => {
      if (id) {
        const market = betData?.markets.value?.[id];

        if (!market) {
          return {
            marketId: "",
            options: [],
            displayName: name[index]
          };
        }

        const homeAndAwayOptions = getHomeAndAwayOptionsForMarket(
          market,
          betData?.options.value
        );

        const events = betData?.events.value || "";
        const sport = events[market.eventId]?.sport || "";

        const orderedOptions = orderedCompetitorData<string>(
          homeAndAwayOptions,
          sport
        );

        return {
          displayName: name[index],
          marketId: id,
          options: orderedOptions
        };
      } else {
        return {
          marketId: "",
          options: [],
          displayName: name[index]
        };
      }
    })
  );
}

export function composeParlayMarketOptions(
  marketIds: Ref<(string | undefined)[]>
) {
  const betData = injectBetData();

  return computed(() => {
    const sports: string[] = [];

    const markets = marketIds.value.map(id => {
      if (id) {
        const market = betData?.markets.value?.[id];

        if (!market) {
          return {
            options: [],
            eventId: ""
          };
        }

        const homeAndAwayOptions = getHomeAndAwayOptionsForMarket(
          market,
          betData?.options.value
        );

        const events = betData?.events.value || "";
        const sport = events[market.eventId]?.sport || "";

        const orderedOptions = orderedCompetitorData<string | undefined>(
          homeAndAwayOptions,
          sport
        );

        if (!sports.includes(sport)) {
          sports.push(sport);
        }

        return {
          marketId: id,
          eventId: market.eventId,
          options: orderedOptions
        };
      } else {
        return {
          options: []
        };
      }
    });

    return {
      markets,
      sports
    };
  });
}

export function composeEventFixture(eventId: string) {
  const event = composeEvent(eventId);
  return computed(() => event.value?.fixture);
}

export function composeEventCompetitors(eventId: string) {
  const event = composeEvent(eventId);
  return computed(
    () =>
      event.value &&
      orderedCompetitorData(event.value.competitors, event.value.sport)
  );
}

export function composeListMarketOptions(marketId: string) {
  const betData = injectBetData();

  return computed(() => {
    if (betData) {
      return betData.markets.value?.[marketId]?.options
        .map(optionId => betData?.options?.value?.[optionId])
        .sort(sortOptionsByOdds);
    }
  });
}

export function composeEventsByDate(): ComputedRef<SportEvent[]> {
  const betData = injectBetData();

  return computed(() => {
    if (!betData) {
      return [];
    }

    const events = betData.events;

    return (
      (betData.events.value &&
        Object.values(events.value as Record<string, SportEvent>).sort(
          (a, b) => {
            if (new Date(a.fixture) < new Date(b.fixture)) {
              return -1;
            }
            if (new Date(a.fixture) > new Date(b.fixture)) {
              return 1;
            }
            return 0;
          }
        )) ||
      []
    );
  });
}

export function insightIsEnabled(
  marketId: string,
  enabledInsights: string[],
  enabledSports?: string[] | undefined
): boolean {
  const availableLeagues: Record<string, string[]> = {
    "American Football": ["NFL"],
    Baseball: ["MLB", "MLB"],
    Basketball: ["NBA"],
    "Ice Hockey": ["NHL", "NHL"]
  };

  const betData = injectBetData();

  if (betData) {
    const market = betData.markets?.value?.[marketId];

    if (market) {
      const event = betData.events?.value?.[market.eventId];
      let isQualifyingCompetition = false;

      if (
        event &&
        availableLeagues[event?.sport || ""] &&
        event.normalisedCompetition
      ) {
        isQualifyingCompetition = availableLeagues[event.sport].includes(
          event.normalisedCompetition
        );
      }

      if (isQualifyingCompetition && event) {
        return enabledInsights.includes(event.normalisedCompetition);
      }

      if (enabledSports?.length && event) {
        return enabledSports.includes(event.sport);
      }
    }
  }

  return false;
}
